import { Component, Input, OnInit } from '@angular/core';
import { ToastService } from '@shared/services/toast.service';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ModalController } from '@ionic/angular';
import { Observable, take } from 'rxjs';
import { environment } from '@environments/environment';
import { Contact } from '@shared/models/contact';
import { Group } from '@shared/models/group';
import { regexList } from '@shared/constants/regex-list';
import { Country } from '@shared/models/country';
import { CountriesService } from '@features/countries/services/countries.service';
import { Lead } from '@shared/models/lead';
import { CustomersService } from '@features/customers/admin/services/customers.service';
import { LoadingState } from '@shared/types/loading-state';

@Component({
  selector: 'app-create-lead-customer',
  templateUrl: './create-lead-customer.component.html',
  standalone: false,
})
export class CreateLeadCustomerComponent implements OnInit {
  @Input() lead: Lead;

  basicDetailsForm: FormGroup;
  addressDetailsForm: FormGroup;
  contactForm: FormGroup;
  countries$: Observable<Country[]>;
  selectedCountry$: Observable<Country | undefined>;
  loadingState$: Observable<LoadingState>;
  parentGroupName: string | undefined;
  isDirectCustomer = false;

  constructor(
    private customersService: CustomersService,
    private modalController: ModalController,
    private countriesService: CountriesService,
    private toastService: ToastService,
  ) {}

  ngOnInit(): void {
    this.countries$ = this.countriesService.getCountriesByTradingEntityAndGroupType(
      this.lead.attributes.trading_entity,
      'customer',
    );
    this.loadingState$ = this.customersService.getCustomersLoading();
    this.setFormData();

    this.updateEntityTypeValidators(this.basicDetailsForm.controls['group_entity_type'].value);
    this.updateAddressValidators();
    this.basicDetailsForm.controls['group_entity_type'].valueChanges.subscribe((entityType) => {
      this.updateEntityTypeValidators(entityType);
    });
    this.basicDetailsForm.controls['crn_exempt'].valueChanges.subscribe(() => {
      this.updateRegistrationNumberValidators();
    });
    this.selectedCountry$ = this.countriesService.getSelectedCountry();
    this.countriesService.selectCountry(this.basicDetailsForm.controls['country_alpha2_code'].value);
    this.updateVatNumberValidators(this.basicDetailsForm.controls['country_alpha2_code'].value);
    this.basicDetailsForm.controls['country_alpha2_code'].valueChanges.subscribe((countryAlpha2Code) => {
      this.updateVatNumberValidators(countryAlpha2Code);
      this.updateAddressValidators();
      this.addressDetailsForm.reset();
      this.countriesService.selectCountry(countryAlpha2Code);
    });
  }

  setFormData(): void {
    this.parentGroupName = this.lead.attributes.parent_group_name || undefined;
    this.isDirectCustomer = this.lead.attributes.parent_group_type === 'distributor';

    this.basicDetailsForm = new FormGroup({
      group_entity_type: new FormControl(this.lead.attributes.group_entity_type, Validators.required),
      name: new FormControl(this.lead.attributes.customer_name, [
        Validators.required,
        Validators.pattern(regexList.group_name),
        Validators.maxLength(100),
      ]),
      country_alpha2_code: new FormControl(
        this.lead.attributes.country_alpha2_code || environment.default_country,
        Validators.required,
      ),
      company_registration_number: new FormControl(this.lead.attributes.company_registration_number),
      crn_exempt: new FormControl(false),
      vat_number: new FormControl(this.lead.attributes.vat_number),
      parent_group_id: new FormControl(this.lead.attributes.parent_group_id, Validators.required),
    });

    this.addressDetailsForm = new FormGroup({
      unit: new FormControl(),
      street_address: new FormControl(),
      suburb: new FormControl(),
      city: new FormControl(),
      province_code: new FormControl(),
      postal_code: new FormControl(),
    });

    this.contactForm = new FormGroup({
      name: new FormControl(this.lead.attributes.contact_name),
      email: new FormControl(this.lead.attributes.contact_email, [Validators.required, Validators.email]),
      telephone_number: new FormControl(this.lead.attributes.contact_telephone_number),
      telephone_dial_code: new FormControl(this.lead.attributes.contact_telephone_dial_code),
    });
  }

  closeModal(): void {
    this.modalController.dismiss({});
  }

  updateEntityTypeValidators(entityType: string): void {
    if (entityType === 'company') {
      this.contactForm.controls['name'].setValidators([Validators.required, Validators.maxLength(100)]);
      this.basicDetailsForm.controls['crn_exempt'].enable({ emitEvent: false });
      this.updateRegistrationNumberValidators();
    } else {
      this.contactForm.controls['name'].clearValidators();
      this.basicDetailsForm.controls['crn_exempt'].setValue(false, { emitEvent: false });
      this.basicDetailsForm.controls['crn_exempt'].disable({ emitEvent: false });
      this.basicDetailsForm.controls['company_registration_number'].reset('', { emitEvent: false });
      this.basicDetailsForm.controls['company_registration_number'].clearValidators();
      this.basicDetailsForm.controls['company_registration_number'].disable({ emitEvent: false });
      this.basicDetailsForm.controls['company_registration_number'].updateValueAndValidity({ emitEvent: false });
    }

    this.contactForm.controls['name'].updateValueAndValidity({ emitEvent: false });
  }

  updateRegistrationNumberValidators(): void {
    if (
      this.basicDetailsForm.controls['group_entity_type'].value !== 'company' ||
      this.basicDetailsForm.controls['crn_exempt'].value
    ) {
      this.basicDetailsForm.controls['company_registration_number'].clearValidators();
      this.basicDetailsForm.controls['company_registration_number'].reset('', { emitEvent: false });
      this.basicDetailsForm.controls['company_registration_number'].disable({ emitEvent: false });
    } else {
      this.basicDetailsForm.controls['company_registration_number'].setValidators([
        Validators.required,
        Validators.pattern(/^(?!.*not available).*/i),
        Validators.minLength(6),
        Validators.maxLength(100),
      ]);
      this.basicDetailsForm.controls['company_registration_number'].enable({ emitEvent: false });
    }

    this.basicDetailsForm.controls['company_registration_number'].updateValueAndValidity({ emitEvent: false });
  }

  updateVatNumberValidators(countryAlpha2Code: string): void {
    this.basicDetailsForm.controls['vat_number'].setValidators([
      Validators.minLength(countryAlpha2Code === 'ZA' ? 9 : 1),
      Validators.maxLength(countryAlpha2Code === 'ZA' ? 10 : 99),
    ]);
    this.basicDetailsForm.controls['vat_number'].updateValueAndValidity({ emitEvent: false });
  }

  updateAddressValidators(): void {
    const requiredFields = ['street_address', 'city', 'province_code'];

    requiredFields.forEach((field) => {
      const control = this.addressDetailsForm.controls[field];
      control.setValidators(this.isDirectCustomer ? Validators.required : []);
      control.updateValueAndValidity({ emitEvent: false });
    });
    this.addressDetailsForm.controls['postal_code'].setValidators(
      this.isDirectCustomer && this.basicDetailsForm.controls['country_alpha2_code'].value === 'ZA'
        ? Validators.required
        : [],
    );
    this.addressDetailsForm.controls['postal_code'].updateValueAndValidity({ emitEvent: false });
  }

  selectParent(parentGroup: Group): void {
    this.parentGroupName = parentGroup.attributes.name;
    this.isDirectCustomer = parentGroup.attributes.group_type === 'distributor';
    this.basicDetailsForm.controls['parent_group_id'].setValue(parentGroup.id);
    this.updateAddressValidators();
  }

  saveCustomer(): void {
    this.basicDetailsForm.markAllAsTouched();
    this.addressDetailsForm.markAllAsTouched();
    this.contactForm.markAllAsTouched();

    if (this.basicDetailsForm.invalid || this.contactForm.invalid || this.addressDetailsForm.invalid) {
      this.toastService.showToast('error', 'Please correct the errors in the form before saving.');
      return;
    }

    this.selectedCountry$.pipe(take(1)).subscribe((country) => {
      const formValues = this.basicDetailsForm.getRawValue();
      const contactValues = this.contactForm.getRawValue();
      const contactName = formValues.group_entity_type === 'person' ? formValues.name : contactValues.name;
      const primaryContact: Contact = {
        name: contactName,
        email: contactValues.email,
        telephone_number: contactValues.telephone_number || undefined,
        telephone_dial_code: contactValues.telephone_number ? String(country?.attributes.dial_code) : undefined,
        contact_roles: ['primary'],
      };
      const group: Partial<Group> = {
        attributes: {
          group_type: 'customer',
          group_entity_type: formValues.group_entity_type,
          name: formValues.name,
          country_alpha2_code: formValues.country_alpha2_code,
          company_registration_number:
            formValues.group_entity_type === 'company'
              ? formValues.company_registration_number || undefined
              : undefined,
          vat_number: formValues.vat_number || undefined,
          parent_group_id: formValues.parent_group_id,
          ...(this.isDirectCustomer ? { address: this.addressDetailsForm.value } : {}),
          ...(this.isDirectCustomer ? { province_code: this.addressDetailsForm.value.province_code } : {}),
          trading_entity: this.lead?.attributes?.trading_entity,
          contacts: [primaryContact],
        },
      } as Partial<Group>;

      this.customersService.createCustomer(group, this.lead.id);
      this.closeModal();
      this.modalController.getTop().then((overlay) => {
        if (overlay?.id === 'add-edit-lead-modal') {
          this.modalController.dismiss(undefined, undefined, 'add-edit-lead-modal');
        }
      });
    });
  }
}
